import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "elasticity-of-demand-indian-examples",
  title: "Elasticity of Demand: Mathematical Formulas, Indian Market Case Studies & Exam Numericals",
  description:
    "Price, income, and cross elasticity of demand explained with Indian market data. Point/Arc calculus derivations, MR-P-Elasticity proofs, 4 worked numericals, Delhi Metro & petrol case studies, and DU exam answer steps.",
  pubDate: "2026-06-18",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["microeconomics", "elasticity", "indian-economy", "demand", "du-syllabus"],
  readMinutes: 18,
  heroEyebrow: "Micro · Core Quantitative Ultra-Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Price elasticity of demand (Ep) measures the responsiveness of quantity demanded of a commodity to a change in its price. In microeconomics papers across Delhi University and central universities, examiners expect both calculus derivations (Point vs Arc Elasticity) and real-world empirical examples from the Indian market documented by{" "}
        <a href="https://mospi.gov.in/" target="_blank" rel="noopener noreferrer">
          MoSPI
        </a>{" "}
        and{" "}
        <a href="https://www.niti.gov.in/" target="_blank" rel="noopener noreferrer">
          NITI Aayog
        </a>
        .
      </p>

      <figure className="my-8">
        <img
          src="/blog/elasticity-diagram.jpg"
          alt="Educational diagram of price elasticity of demand curves showing elastic demand curve with shallow slope, inelastic demand curve with steep slope, and unit elastic demand."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: Demand Curve Elasticity Spectrum. Steep demand curves represent inelastic demand (|Ep| &lt; 1), shallow demand curves represent elastic demand (|Ep| &gt; 1), and rectangular hyperbolas represent unit elastic demand (|Ep| = 1).
        </figcaption>
      </figure>

      <h2>1. Mathematical Formulations: Point vs. Arc Elasticity</h2>

      <h3>Point Price Elasticity (Calculus Formulation)</h3>
      <p>For a continuous demand function Q = f(P), point elasticity at a specific price-quantity point is:</p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Ep = (% Change in Q) / (% Change in P) = (dQ / dP) × (P / Q)
      </p>

      <h3>Arc Price Elasticity (Midpoint Formula)</h3>
      <p>When measuring elasticity over a discrete price interval between (P1, Q1) and (P2, Q2):</p>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Earc = [(Q2 - Q1) / ((Q1 + Q2)/2)] ÷ [(P2 - P1) / ((P1 + P2)/2)] = [(Q2 - Q1)/(P2 - P1)] × [(P1 + P2)/(Q1 + Q2)]
      </p>

      <h2>2. Mathematical Proof: Marginal Revenue, Price, &amp; Elasticity</h2>
      <p>Total Revenue TR is defined as TR = P × Q, where P = f(Q) is the inverse demand function.</p>
      <p>Marginal Revenue MR is the derivative of Total Revenue with respect to Q:</p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        MR = d(TR)/dQ = d(P × Q)/dQ = P + Q × (dP/dQ)
        <br />
        Factoring out P:
        <br />
        MR = P × [ 1 + (Q / P) × (dP / dQ) ]
        <br />
        Since Ep = (dQ / dP) × (P / Q), we have (Q / P) × (dP / dQ) = 1 / Ep:
      </p>
      <p className="font-mono bg-card p-3 border border-rule text-center text-grade font-semibold">
        MR = P × [ 1 - 1 / |Ep| ]
      </p>

      <h3>Implications of the MR Formula:</h3>
      <ul>
        <li><strong>If |Ep| &gt; 1 (Elastic):</strong> 1 / |Ep| &lt; 1 ⟹ MR &gt; 0. Lowering price increases total revenue.</li>
        <li><strong>If |Ep| = 1 (Unit Elastic):</strong> 1 / |Ep| = 1 ⟹ MR = 0. Total revenue is maximized.</li>
        <li><strong>If |Ep| &lt; 1 (Inelastic):</strong> 1 / |Ep| &gt; 1 ⟹ MR &lt; 0. Lowering price decreases total revenue.</li>
      </ul>

      <h2>3. Worked 10-Mark Exam Numericals</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 1 (Linear Demand Optimization)</strong>
        <p className="mt-2 font-mono">
          Given linear demand function Q = 500 - 20P:
          <br />
          (a) Calculate point price elasticity at P = 10.
          <br />
          (b) Calculate arc price elasticity when price increases from P1 = 10 to P2 = 15.
          <br />
          (c) Determine the total revenue maximizing price P_TR_max. [10 Marks]
        </p>
      </div>

      <h3>Solution Breakdown</h3>
      <ol className="space-y-2 text-sm font-mono list-decimal list-inside">
        <li>
          <strong>Part (a): Point Elasticity at P = 10</strong>
          <br />
          At P = 10, Q = 500 - 20(10) = 300. Derivative dQ/dP = -20.
          <br />
          Ep = -20 × (10 / 300) = -200 / 300 = -0.67 (Inelastic, |Ep| &lt; 1).
        </li>
        <li>
          <strong>Part (b): Arc Elasticity between P1 = 10 (Q1 = 300) and P2 = 15 (Q2 = 200)</strong>
          <br />
          Earc = [(200 - 300) / (15 - 10)] × [(10 + 15) / (300 + 200)] = [-100 / 5] × [25 / 500] = -20 × 0.05 = -1.0 (Unit Elastic).
        </li>
        <li>
          <strong>Part (c): Total Revenue Maximizing Price</strong>
          <br />
          Total Revenue TR = P × Q = P(500 - 20P) = 500P - 20P².
          <br />
          dTR/dP = 500 - 40P = 0 ⟹ P_TR_max = 12.5.
        </li>
      </ol>

      <h2>4. Empirical Indian Market Case Studies</h2>
      <ul>
        <li>
          <strong>Inelastic Demand (|Ep| &lt; 1): Petrol &amp; Essential Medicines</strong>
          <br />
          Data published by the Petroleum Planning &amp; Analysis Cell under the{" "}
          <a href="https://mopng.gov.in/" target="_blank" rel="noopener noreferrer">
            Ministry of Petroleum and Natural Gas
          </a>{" "}
          shows retail fuel demand in urban India exhibits low short-run price elasticity (~ -0.2). Commuters have limited immediate public transit substitutes, making fuel consumption price-inelastic.
        </li>
        <li>
          <strong>Cross-Price Elasticity (Exy &gt; 0): Delhi Metro vs DTC Buses</strong>
          <br />
          When Delhi Metro fare revisions were implemented, commuter demand showed positive cross-elasticity between metro transit and municipal bus lines operated by DTC, illustrating classic substitute dynamics.
        </li>
      </ul>

      <h2>5. Related Micro &amp; Quantitative Guides</h2>
      <p>
        For further practice on mathematical microeconomics and calculus techniques, check our guides on{" "}
        <a href="/blog/mme-du-economics-guide" className="font-semibold underline text-grade">
          MME Survival &amp; Distinction Guide
        </a>{" "}
        and{" "}
        <a href="/blog/gdp-deflator-vs-cpi" className="font-semibold underline text-grade">
          GDP Deflator vs CPI Inflation Analysis
        </a>
        .
      </p>
    </>
  );
}

